import { createContext, useContext, useState, useMemo, useCallback, useEffect } from "react";
import { SELECTED_STORE_KEY } from "../const/inventario";

// =======================
// Contexto global
// =======================
const StoresContext = createContext(null);

/**
 * Provider global para el estado de almacenes.
 * Controla el estado global (stores, selectedStore)
 * y expone operaciones CRUD con respuesta estructurada.
 */
export const StoresProvider = ({ children }) => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("Todos");
  const [selectedStoreData, setSelectedStoreData] = useState(() => {
    if (typeof window === "undefined") return null;

    const stored = window.sessionStorage.getItem(SELECTED_STORE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (err) {
        console.error("Error al parsear almacén guardado:", err);
        return null;
      }
    }
    return null;
  });

  // Persistir cambios de selectedStoreData
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (selectedStoreData) {
      window.sessionStorage.setItem(SELECTED_STORE_KEY, JSON.stringify(selectedStoreData));
    }
  }, [selectedStoreData]);

  //* SETTERS
  /** Reemplaza completamente la lista de almacenes */
  const resetStores = useCallback((newStores) => {
    setStores(newStores);
  }, []);

  /** Wrapper para setSelectedStoreData que también actualiza selectedStore */
  const updateSelectedStoreData = useCallback((newStoreData) => {
    setSelectedStoreData(newStoreData);
    if (newStoreData) {
      setSelectedStore(newStoreData.name);
    }
  }, []);

  /** Actualiza un almacén específico por ID */
  const updateStoreById = useCallback((storeId, updates) => {
    setStores((prev) =>
      prev.map((store) => (store.id === storeId ? { ...store, ...updates } : store))
    );

    setSelectedStoreData((prev) => {
      if (prev && prev.id === storeId) {
        return { ...prev, ...updates };
      }
      return prev;
    });
  }, []);

  /** Agrega un nuevo almacén */
  const addStore = useCallback((newStore) => {
    setStores((prev) => [...prev, newStore]);
  }, []);

  /** Elimina un almacén por ID */
  const removeStore = useCallback(
    (storeId) => {
      setStores((prev) => prev.filter((store) => store.id !== storeId));

      // Si se elimina el almacén actual, resetear a "Todos"
      setSelectedStoreData((prev) => {
        if (prev && prev.id === storeId) {
          return stores.find((s) => s.name === "Todos") || null;
        }
        return prev;
      });
    },
    [stores]
  );

  /** Incrementa/decrementa el productCount de un almacén */
  const updateProductCount = useCallback((storeId, delta) => {
    setStores((prev) =>
      prev.map((store) =>
        store.id === storeId
          ? {
              ...store,
              productCount: Math.max(0, (store.productCount || 0) + delta),
            }
          : store
      )
    );
  }, []);

  const value = useMemo(
    () => ({
      // Estado
      stores,
      selectedStore,
      selectedStoreData,

      // Setters directos
      setStores,
      setSelectedStore,
      setSelectedStoreData: updateSelectedStoreData,

      // Operaciones primitivas
      resetStores,
      updateStoreById,
      addStore,
      removeStore,
      updateProductCount,
    }),
    [
      stores,
      selectedStore,
      selectedStoreData,
      updateSelectedStoreData,
      resetStores,
      updateStoreById,
      addStore,
      removeStore,
      updateProductCount,
    ]
  );

  return <StoresContext.Provider value={value}>{children}</StoresContext.Provider>;
};

// =======================
// Hook consumidor
// =======================
export const useStores = () => {
  const context = useContext(StoresContext);
  if (!context) {
    throw new Error("useStores debe usarse dentro de un StoresProvider");
  }
  return context;
};
